import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./lib/ProtectedRoute";

import HomePage from "./pages/marketing/HomePage";
import ServiceLandingPage, { ServicesHub } from "./pages/marketing/ServiceLandingPage";
import PartnerLanding from "./pages/marketing/PartnerLanding";
import PartnerHygieneLanding from "./pages/marketing/PartnerHygieneLanding";
import PartnerBmwLanding from "./pages/marketing/PartnerBmwLanding";
import PageImagesCms from "./pages/admin/PageImagesCms";
import InvoiceAnalytics from "./pages/finance/invoice-analytics/InvoiceAnalytics";
import HealthLanding from "./pages/marketing/HealthLanding";
import HygieneLanding from "./pages/marketing/HygieneLanding";
import GovernmentPortal from "./pages/marketing/GovernmentPortal";
import Terms from "./pages/marketing/Terms";
import Refund from "./pages/marketing/Refund";
import Privacy from "./pages/marketing/Privacy";
import AdminEntry from "./pages/AdminEntry";
import WorkLogin from "./pages/WorkLogin";
import Dashboard from "./pages/Dashboard";
import Locations from "./pages/master/Locations";
import Zones from "./pages/master/Zones";
import Schedules from "./pages/master/Schedules";
import DropLocations from "./pages/master/DropLocations";
import SpecialCollectionDates from "./pages/master/SpecialCollectionDates";
import { ServiceCategoriesPage, ServicesPage, WasteTypesPage, ChargesPage, ProductsPage } from "./pages/pricing/CrudPages";
import Packages from "./pages/pricing/Packages";
import DistancePricing from "./pages/pricing/DistancePricing";
import Calculator from "./pages/pricing/Calculator";
import WasteReport from "./pages/pricing/WasteReport";
import CustomerList from "./pages/customers/CustomerList";
import CustomerDetail from "./pages/customers/CustomerDetail";
import PublicRegister from "./pages/customers/PublicRegister";
import AuditLogs from "./pages/AuditLogs";
import ModuleStub from "./pages/ModuleStub";
import StaffList from "./pages/staff/StaffList";
import WorkforceLeaderboard from "./pages/workforce/WorkforceLeaderboard";
import WorkforceShifts from "./pages/workforce/WorkforceShifts";
import WorkforcePayroll from "./pages/workforce/WorkforcePayroll";
import WorkforceStaffProfile from "./pages/workforce/WorkforceStaffProfile";
import GovLayout from "./pages/gov/GovLayout";
import GovLogin from "./pages/gov/GovLogin";
import GovDashboard from "./pages/gov/GovDashboard";
import GovCustomers from "./pages/gov/GovCustomers";
import GovWasteReport from "./pages/gov/GovWasteReport";
import GovComplianceMap from "./pages/gov/GovComplianceMap";
import GovComplaints from "./pages/gov/GovComplaints";
import GovAirq from "./pages/gov/GovAirq";
import GovCsr from "./pages/gov/GovCsr";
import {
  GovRegulatoryDashboard, GovBMWReports, GovCompliance, GovAuditTrail,
  GovCertificateVerification, GovSustainability, GovProfile,
} from "./pages/gov/GovPages";
import GovUserManagement from "./pages/admin/GovUserManagement";
import TrainingPartners from "./pages/admin/TrainingPartners";
import ContentManagement from "./pages/admin/ContentManagement";
import ServicesPageCms from "./pages/admin/ServicesPageCms";
import AdminAboutEditor from "./pages/admin/AdminAboutEditor";
import AdminOTPSettings from "./pages/admin/AdminOTPSettings";
import CustomerAppCms from "./pages/admin/CustomerAppCms";
import EagleEye from "./pages/admin/EagleEye";
import AdBannerManager from "./pages/admin/AdBannerManager";
import AlarmSoundManager from "./pages/admin/AlarmSoundManager";
import AdminNavMenus from "./pages/admin/AdminNavMenus";
import InstitutionalCerts from "./pages/admin/InstitutionalCerts";
import DataSync from "./pages/admin/DataSync";
import Careers from "./pages/marketing/Careers";
import AdminCareers from "./pages/admin/AdminCareers";
import AdminManualPayments from "./pages/admin/AdminManualPayments";
import AdminAgencies from "./pages/admin/AdminAgencies";
import AdminRegCerts from "./pages/admin/AdminRegCerts";
import CustomerRegistrationCerts from "./pages/customers/CustomerRegistrationCerts";
import MediaLibrary from "./pages/admin/MediaLibrary";
import ForGovernment from "./pages/marketing/ForGovernment";
import ForCompanies from "./pages/marketing/ForCompanies";
import ForHealthcare from "./pages/marketing/ForHealthcare";
import Investors from "./pages/marketing/Investors";
import CSRSustainability from "./pages/marketing/CSRSustainability";
import SecurityCompliance from "./pages/marketing/SecurityCompliance";
import Contact from "./pages/marketing/Contact";
import AboutUs from "./pages/marketing/AboutUs";
import { PublicationsListing, PublicationDetail } from "./pages/marketing/Publications";
import ComingSoon from "./pages/marketing/ComingSoon";
import NotFound from "./pages/marketing/NotFound";
import SegregationGame from "./pages/game/SegregationGame";
import SDG2030 from "./pages/marketing/SDG2030";
import SitePages from "./pages/admin/SitePages";
import ServicePartnersAdmin from "./pages/admin/ServicePartners";
import Hospitals from "./pages/admin/Hospitals";
import EmDispatchLogin from "./pages/emdispatch/EmDispatchLogin";
import EmDispatchConsole from "./pages/emdispatch/EmDispatchConsole";
import EmDispatchLanding from "./pages/emdispatch/EmDispatchLanding";
import PartnerPortalLogin from "./pages/partner_portal/PartnerLogin";
import PartnerPortalDashboard from "./pages/partner_portal/PartnerDashboard";
import PartnerListings from "./pages/partner_portal/PartnerListings";
import PartnerOrders from "./pages/partner_portal/PartnerOrders";
import PartnerDeliveries from "./pages/partner_portal/PartnerDeliveries";
import PartnerHealth from "./pages/partner_portal/PartnerHealth";
import PartnerWave4 from "./pages/partner_portal/PartnerWave4";
import MeOrders from "./pages/me/MeOrders";
import { CourierAuthExchange, CourierLogin, CourierHome, CourierTaskDetail } from "./pages/courier/CourierApp";
import { SitePagesProvider, useIsPageEnabled, useHomePath } from "./lib/sitePages";
import {
  PartnerLogin, PartnerBrandedLogin, PartnerLayout, PartnerOverview, PartnerUsers, PartnerPrograms,
  PartnerProgress, PartnerCertificates, PartnerBranding, PartnerBilling,
  PartnerReports, PartnerAudit, HospitalLanding,
} from "./pages/partner/PartnerPages";
import OfficeStaffList from "./pages/admin/OfficeStaffList";
import OfficeStaffForm from "./pages/admin/OfficeStaffForm";
import OfficeStaffProfile from "./pages/admin/OfficeStaffProfile";
import OfficeAttendance from "./pages/admin/OfficeAttendance";
import OfficePayrollSummary from "./pages/admin/OfficePayrollSummary";
import RolesPermissions from "./pages/admin/RolesPermissions";
import BreakActivityLogs from "./pages/admin/BreakActivityLogs";
import PerformanceTracking from "./pages/admin/PerformanceTracking";
import MyDuty from "./pages/admin/MyDuty";
import ServiceRequestList from "./pages/operations/ServiceRequestList";
import SrAlertHistory from "./pages/operations/SrAlertHistory";
import Assignments from "./pages/operations/Assignments";
import OperationsTiming from "./pages/operations/OperationsTiming";
import SkipAnalytics from "./pages/operations/SkipAnalytics";
import ServiceRequestForm from "./pages/operations/ServiceRequestForm";
import ServiceRequestDetail from "./pages/operations/ServiceRequestDetail";
import DailyServiceRequests from "./pages/operations/DailyServiceRequests";
import RouteJobsList from "./pages/operations/RouteJobsList";
import RouteCreate from "./pages/operations/RouteCreate";
import AISuggestRoutes from "./pages/operations/AISuggestRoutes";
import RouteJobDetail from "./pages/operations/RouteJobDetail";
import RouteDailyView from "./pages/operations/RouteDailyView";
import PublicEbill from "./pages/public/PublicEbill";
import QuickPay    from "./pages/public/QuickPay";
import ReferralAffiliate from "./pages/customers/ReferralAffiliate";
import PartnersList from "./pages/customers/PartnersList";
import PartnerProfile from "./pages/customers/PartnerProfile";
import ReferralPerformance from "./pages/customers/ReferralPerformance";
import CustomerRewards from "./pages/customers/CustomerRewards";
import FleetMgmt from "./pages/fleet/FleetMgmt";
import Operations from "./pages/operations/Operations";
import LiveDashboard from "./pages/live/LiveDashboard";
import FindGpsDashboard from "./pages/findgps/FindGpsDashboard";
import GigPartnersAdmin from "./pages/gig-partners/GigPartnersAdmin";
import OnboardingManagersAdmin from "./pages/gig-partners/OnboardingManagersAdmin";
import PartnerPayouts from "./pages/gig-partners/PartnerPayouts";
import PartnerAppLogin from "./pages/partner-app/PartnerAppLogin";
import PartnerAppShell from "./pages/partner-app/PartnerAppShell";
import QrInventory from "./pages/qr/QrInventory";
import DisposalCenter from "./pages/disposal/DisposalCenter";
import FraudDashboard from "./pages/fraud/FraudDashboard";
import Invoices from "./pages/finance/Invoices";
import InvoiceThermal from "./pages/finance/InvoiceThermal";
import InvoicesPending from "./pages/finance/InvoicesPending";
import Transactions from "./pages/finance/Transactions";
import Revenue from "./pages/finance/Revenue";
import FinanceRollup from "./pages/finance/FinanceRollup";
import FinanceSettings from "./pages/finance/FinanceSettings";
import RazorpayPayments from "./pages/finance/RazorpayPayments";
import WalletTransactions from "./pages/finance/WalletTransactions";
import HealthServicesCommand from "./pages/health-services/HealthServicesCommand";
import AmbulanceDispatch from "./pages/ambulance/AmbulanceDispatch";
import AmbulancePublicPortal from "./pages/ambulance/AmbulancePublicPortal";
import DailyCareAnalytics from "./pages/analytics/DailyCareAnalytics";
import HealthOpsExtras from "./pages/health-services/HealthOpsExtras";
import OpBookingWidget from "./pages/health-services/OpBookingWidget";
import PharmacyOpsConsole from "./pages/health-services/PharmacyOpsConsole";
import NurseMobileView from "./pages/health-services/NurseMobileView";
import Wave3Console from "./pages/health-services/Wave3Console";
import Wave4Console from "./pages/health-services/Wave4Console";
import AirqSuperAdmin from "./pages/health-services/AirqSuperAdmin";
import { AirqAccountLogin, AirqAccountDashboard } from "./pages/airq-account/AirqAccountPortal";
import AirqLanding from "./pages/airq-account/AirqLanding";
import AirqGovLogin from "./pages/airq-account/AirqGovLogin";
import EmailLog from "./pages/finance/EmailLog";
import Expenses from "./pages/finance/Expenses";
import DailyBriefing from "./pages/operations/DailyBriefing";
import Leads from "./pages/crm/Leads";
import ContactSubmissions from "./pages/crm/ContactSubmissions";
import Tickets from "./pages/crm/Tickets";
import Chats from "./pages/crm/Chats";
import CrmTasks from "./pages/crm/CrmTasks";
import StaffTeamTasks from "./pages/staff-app/StaffTeamTasks";
import HandoffQueue from "./pages/crm/HandoffQueue";
import PhoneCRM from "./pages/crm/PhoneCRM";
import PhoneReport from "./pages/crm/PhoneReport";
import PhonePasscodes from "./pages/crm/PhonePasscodes";
import Analytics from "./pages/Analytics";
import Certificates from "./pages/Certificates";
import SystemSettings from "./pages/settings/SystemSettings";
import PrinterSettings from "./pages/settings/PrinterSettings";
import ScaleSettings from "./pages/settings/ScaleSettings";
import Franchise from "./pages/franchise/Franchise";
import MeLogin from "./pages/me/MeLogin";
import MeLayout from "./pages/me/MeLayout";
import MeDashboard from "./pages/me/MeDashboard";
import MeBookings from "./pages/me/MeBookings";
import MeTracking, { MeTrackingList } from "./pages/me/MeTracking";
import MeDues from "./pages/me/MeDues";
import MeCertificates from "./pages/me/MeCertificates";
import MeRequests from "./pages/me/MeRequests";
import MeAffiliate from "./pages/me/MeAffiliate";
import { MeServicesHub, MeServicePartners, MePartnerDetail } from "./pages/me/MeServices";
import {
  MeHealthHub, DoctorList, DoctorDetail, LabList, LabDetail,
  AmbulanceList, AmbulanceBook, MyHealthBookings,
  VaccineReminders, PillReminders,
} from "./pages/me/MeHealth";
import { VitalsTracker, BabyTracker, EmergencyContacts } from "./pages/me/MeCare";
import { CycleTracker } from "./pages/me/MeCycle";
import MeCycleAnalysis from "./pages/me/MeCycleAnalysis";
import { PregnancyHome } from "./pages/me/MePregnancy";
import { BabyGrowth } from "./pages/me/MeGrowth";
import { PetHome } from "./pages/me/MePet";
import { HomeInventory, CareCircle } from "./pages/me/MeInventory";
import { ElderCare, KidsCare } from "./pages/me/MeElderKids";
import MeWallet from "./pages/me/MeWallet";
import MeHygieneHub from "./pages/me/MeHygieneHub";
import MeVerificationGate from "./pages/me/MeVerificationGate";
import MeHealthLanding from "./pages/me/MeHealthLanding";
import MeDailyCareHub from "./pages/me/MeDailyCareHub";
import MeSettings from "./pages/me/MeSettings";
import CustomerWalletManagement from "./pages/customers/CustomerWalletManagement";
import MeProfile from "./pages/me/MeProfile";
import MeChat from "./pages/me/MeChat";
import MeCustomerCare from "./pages/me/MeCustomerCare";
import ChatBotSettings from "./pages/settings/ChatBotSettings";
import ReplyTemplates from "./pages/settings/ReplyTemplates";
import TicketsArchive from "./pages/crm/TicketsArchive";
import AuditTicketPurges from "./pages/admin/AuditTicketPurges";
import PublicChatWidget from "./components/PublicChatWidget";
import GlobalSrChime from "./components/GlobalSrChime";
import PublicPartner from "./pages/public/PublicPartner";
import { CustomerAuthProvider, useCustAuth } from "./lib/customerAuth";
import { StaffAuthProvider } from "./lib/staffAuth";
import StaffLogin from "./pages/staff-app/StaffLogin";
import StaffLayout from "./pages/staff-app/StaffLayout";
import StaffDuty from "./pages/staff-app/StaffDuty";
import StaffTasks from "./pages/staff-app/StaffTasks";
import StaffTaskDetail from "./pages/staff-app/StaffTaskDetail";
import StaffPOS from "./pages/staff-app/StaffPOS";
import StaffDeliveries from "./pages/staff-app/StaffDeliveries";
import TrainingAcademy from "./pages/training/TrainingAcademy";
import {
  TrainingLogin, RequiredHome, RequiredCourse, RequiredExam, TrainingVerify,
} from "./pages/training/TrainingPortal";
import TrainingAdmin from "./pages/admin/TrainingAdmin";

const P = ({ children, roles, perm }) => (
  <ProtectedRoute roles={roles} perm={perm}>{children}</ProtectedRoute>
);

// Public-page visibility guard: renders ComingSoon when the page has been
// disabled via /admin/site-pages.
const G = ({ path, label, children }) => {
  const enabled = useIsPageEnabled(path);
  return enabled ? children : <ComingSoon pageLabel={label}/>;
};

// Root ("/") gate — if the super-admin picked a different Main-group page as
// the homepage, redirect there. Otherwise render the default HomePage.
const RootGate = () => {
  const homePath = useHomePath();
  const homeEnabled = useIsPageEnabled("/");
  if (homePath && homePath !== "/") {
    return <Navigate to={homePath} replace/>;
  }
  return homeEnabled ? <HomePage/> : <ComingSoon pageLabel="Home"/>;
};

// Iter 220 — Gate for modules that need a verified customer account.
// Hygiene is behind this; Health / Daily Care / Profile remain open.
const VerifiedCustomerOnly = ({ moduleLabel, children }) => {
  const { customer } = useCustAuth();
  const status = (customer?.status || "").toLowerCase();
  if (status && status !== "active") {
    return <MeVerificationGate moduleLabel={moduleLabel} />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      {/* Global default SEO — per-page <SEO/> overrides these */}
      <Helmet defaultTitle="Rescigo Health — Biomedical waste collection Kerala · CPCB compliant"
              titleTemplate="%s">
        <html lang="en" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      </Helmet>
      <BrowserRouter>
        <SitePagesProvider>
        <AuthProvider>
          <CustomerAuthProvider>
          <StaffAuthProvider>
            <Toaster position="top-right" richColors />
            {/* Iter 92 — Public "Rigo" chat widget for guest visitors.
                Reads /public/chat/config and hides itself when the
                super-admin flips public_chat_enabled off. */}
            <PublicChatWidget />
            <GlobalSrChime />
            <Routes>
              <Route path="/" element={<RootGate/>} />
              <Route path="/health" element={<G path="/health" label="Health Services"><HealthLanding /></G>} />
              <Route path="/hygiene" element={<G path="/hygiene" label="Hygiene Services"><HygieneLanding /></G>} />
              <Route path="/emdispatch" element={<EmDispatchLanding/>}/>
              <Route path="/emdispatch/login" element={<EmDispatchLogin/>}/>
              <Route path="/emdispatch/console" element={<EmDispatchConsole/>}/>
              <Route path="/government" element={<GovernmentPortal />} />
              <Route path="/for-government" element={<G path="/for-government" label="For Government"><ForGovernment /></G>} />
              <Route path="/for-companies" element={<G path="/for-companies" label="For Companies"><ForCompanies /></G>} />
              <Route path="/for-healthcare" element={<G path="/for-healthcare" label="For Healthcare"><ForHealthcare /></G>} />
              <Route path="/investors" element={<G path="/investors" label="Investor Relations"><Investors /></G>} />
              <Route path="/csr-sustainability" element={<G path="/csr-sustainability" label="CSR & Sustainability"><CSRSustainability /></G>} />
              <Route path="/security-compliance" element={<G path="/security-compliance" label="Security & Compliance"><SecurityCompliance /></G>} />
              <Route path="/contact" element={<G path="/contact" label="Contact"><Contact /></G>} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/about" element={<G path="/about" label="About Us"><AboutUs /></G>} />
              <Route path="/services" element={<ServicesHub />} />
              <Route path="/partner" element={<PartnerLanding />} />
              <Route path="/partner-hygiene" element={<PartnerHygieneLanding />} />
              <Route path="/partner-bmw" element={<PartnerBmwLanding />} />
              <Route path="/partner-with-us" element={<PartnerLanding />} />
              <Route path="/become-a-partner" element={<PartnerLanding />} />
              <Route path="/partners" element={<PartnerLanding />} />
              <Route path="/services/:serviceSlug" element={<ServiceLandingPage />} />
              <Route path="/services/:serviceSlug/:citySlug" element={<ServiceLandingPage />} />
              <Route path="/newsroom" element={<G path="/newsroom" label="Newsroom"><PublicationsListing variant="newsroom" /></G>} />
              <Route path="/newsroom/:slug" element={<G path="/newsroom" label="Newsroom"><PublicationDetail variant="newsroom" /></G>} />
              <Route path="/white-papers" element={<G path="/white-papers" label="White Papers"><PublicationsListing variant="white_papers" /></G>} />
              <Route path="/white-papers/:slug" element={<G path="/white-papers" label="White Papers"><PublicationDetail variant="white_papers" /></G>} />
              <Route path="/terms" element={<G path="/terms" label="Terms of Service"><Terms /></G>} />
              <Route path="/refund" element={<G path="/refund" label="Refund Policy"><Refund /></G>} />
              <Route path="/privacy" element={<G path="/privacy" label="Privacy Policy"><Privacy /></G>} />
              <Route path="/404" element={<NotFound />} />
              <Route path="/game" element={<G path="/game" label="Segregation Challenge"><SegregationGame /></G>} />
              <Route path="/sdg2030" element={<G path="/sdg2030" label="SDG 2030"><SDG2030 /></G>} />
              <Route path="/adentry" element={<AdminEntry />} />
              <Route path="/work" element={<WorkLogin />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/register" element={<G path="/register" label="Public Register"><PublicRegister /></G>} />

              {/* Public e-bill (no auth) */}
              <Route path="/ebill/:invoiceNo" element={<PublicEbill />} />
              <Route path="/quickpay" element={<QuickPay />} />

              {/* Public partner profile (no auth) */}
              <Route path="/p/:partnerId" element={<PublicPartner />} />

              {/* Customer mobile app */}
              <Route path="/me/login" element={<MeLogin />} />
              <Route path="/me" element={<MeLayout />}>
                <Route index element={<MeDashboard />} />
                {/* Iter 220f — Hygiene service pages are ALL gated behind
                    account verification (not just the /me/hygiene hub).
                    Requests, bookings, tracking, dues and certificates
                    all deal with waste-management workflows that need
                    a verified account. Health / Wallet / Profile /
                    Chat / marketplace remain open. */}
                <Route path="bookings" element={<VerifiedCustomerOnly moduleLabel="Hygiene Bookings"><MeBookings /></VerifiedCustomerOnly>} />
                <Route path="requests" element={<VerifiedCustomerOnly moduleLabel="Pickup Requests"><MeRequests /></VerifiedCustomerOnly>} />
                <Route path="tracking" element={<VerifiedCustomerOnly moduleLabel="Pickup Tracking"><MeTrackingList /></VerifiedCustomerOnly>} />
                <Route path="tracking/:id" element={<VerifiedCustomerOnly moduleLabel="Pickup Tracking"><MeTracking /></VerifiedCustomerOnly>} />
                <Route path="dues" element={<VerifiedCustomerOnly moduleLabel="Hygiene Dues"><MeDues /></VerifiedCustomerOnly>} />
                <Route path="certificates" element={<VerifiedCustomerOnly moduleLabel="Certificates"><MeCertificates /></VerifiedCustomerOnly>} />
                <Route path="affiliate" element={<MeAffiliate />} />
                <Route path="profile" element={<MeProfile />} />
                <Route path="chat" element={<MeChat />} />
                <Route path="customer-care" element={<MeCustomerCare />} />
                <Route path="services" element={<MeServicesHub />} />
                <Route path="services/partner/:id" element={<MePartnerDetail />} />
                <Route path="services/:subcategory" element={<MeServicePartners />} />
                <Route path="hygiene" element={<VerifiedCustomerOnly moduleLabel="Hygiene"><MeHygieneHub /></VerifiedCustomerOnly>} />
                <Route path="care" element={<MeDailyCareHub />} />
                <Route path="settings" element={<MeSettings />} />
                <Route path="health" element={<MeHealthLanding />} />
                <Route path="health/doctors" element={<DoctorList />} />
                <Route path="health/doctors/:pid" element={<DoctorDetail />} />
                <Route path="health/labs" element={<LabList />} />
                <Route path="health/labs/:pid" element={<LabDetail />} />
                <Route path="health/ambulance" element={<AmbulanceList />} />
                <Route path="health/ambulance/:pid" element={<AmbulanceBook />} />
                <Route path="health/bookings" element={<MyHealthBookings />} />
                <Route path="health/vaccines" element={<VaccineReminders />} />
                <Route path="health/pills" element={<PillReminders />} />
                <Route path="health/vitals" element={<VitalsTracker />} />
                <Route path="health/baby" element={<BabyTracker />} />
                <Route path="health/emergency" element={<EmergencyContacts />} />
                <Route path="health/cycle" element={<CycleTracker />} />
                <Route path="health/cycle/analysis" element={<MeCycleAnalysis />} />
                <Route path="health/pregnancy" element={<PregnancyHome />} />
                <Route path="health/growth" element={<BabyGrowth />} />
                <Route path="health/pet" element={<PetHome />} />
                <Route path="health/inventory" element={<HomeInventory />} />
                <Route path="health/circle" element={<CareCircle />} />
                <Route path="health/elder" element={<ElderCare />} />
                <Route path="health/kids" element={<KidsCare />} />
                <Route path="wallet" element={<MeWallet />} />
                <Route path="orders" element={<MeOrders />} />
              </Route>

              {/* Field staff mobile app */}
              <Route path="/staff-app/login" element={<StaffLogin />} />
              <Route path="/staff-app" element={<StaffLayout />}>
                <Route index element={<StaffDuty />} />
                <Route path="tasks" element={<StaffTasks />} />
                <Route path="tasks/:id" element={<StaffTaskDetail />} />
                <Route path="team" element={<StaffTeamTasks />} />
                <Route path="deliveries" element={<StaffDeliveries />} />
                <Route path="pos" element={<StaffPOS />} />
                <Route path="airq" element={<Navigate to="/staff-app" replace />} />
                <Route path="printer" element={<PrinterSettings />} />
                <Route path="scale" element={<ScaleSettings />} />
              </Route>

              {/* Training & Certification Academy (public + trainee) */}
              <Route path="/training" element={<G path="/training" label="Training Academy"><TrainingAcademy /></G>} />
              <Route path="/training/login" element={<TrainingLogin />} />
              <Route path="/training/home" element={<RequiredHome />} />
              <Route path="/training/course/:courseId" element={<RequiredCourse />} />
              <Route path="/training/exam/:courseId" element={<RequiredExam />} />
              <Route path="/training/verify" element={<G path="/training/verify" label="Certificate Verification"><TrainingVerify /></G>} />
              <Route path="/training/verify/:certNo" element={<G path="/training/verify" label="Certificate Verification"><TrainingVerify /></G>} />

              <Route path="/dashboard" element={<P><Dashboard /></P>} />
              <Route path="/coordinator" element={<Navigate to="/dashboard" replace />} />

            {/* Training Academy — Admin Configuration */}
            <Route path="/training-admin" element={<P roles={["superadmin","admin","manager"]}><TrainingAdmin /></P>} />

            {/* Master Control (admin/manager only) */}
            <Route path="/master/locations" element={<P roles={["superadmin","admin","manager"]}><Locations /></P>} />
            <Route path="/master/zones" element={<P roles={["superadmin","admin","manager"]}><Zones /></P>} />
            <Route path="/master/schedules" element={<P roles={["superadmin","admin","manager"]}><Schedules /></P>} />
            <Route path="/master/special-dates" element={<P roles={["superadmin","admin","manager"]}><SpecialCollectionDates /></P>} />
            <Route path="/master/drop-locations" element={<P roles={["superadmin","admin","manager"]}><DropLocations /></P>} />

            {/* Pricing (admin/manager only) */}
            <Route path="/pricing/service-categories" element={<P roles={["superadmin","admin","manager"]}><ServiceCategoriesPage /></P>} />
            <Route path="/pricing/services" element={<P roles={["superadmin","admin","manager"]}><ServicesPage /></P>} />
            <Route path="/pricing/waste-types" element={<P roles={["superadmin","admin","manager"]}><WasteTypesPage /></P>} />
            <Route path="/pricing/waste-report" element={<P roles={["superadmin","admin","manager"]}><WasteReport /></P>} />
            <Route path="/pricing/charges" element={<P roles={["superadmin","admin","manager"]}><ChargesPage /></P>} />
            <Route path="/pricing/products" element={<P roles={["superadmin","admin","manager"]}><ProductsPage /></P>} />
            <Route path="/pricing/packages" element={<P roles={["superadmin","admin","manager"]}><Packages /></P>} />
            <Route path="/pricing/distance" element={<P roles={["superadmin","admin","manager"]}><DistancePricing /></P>} />
            <Route path="/pricing/calculator" element={<P roles={["superadmin","admin","manager"]}><Calculator /></P>} />

            {/* Customers */}
            <Route path="/customers" element={<P><CustomerList /></P>} />
            <Route path="/customers/verify" element={<P><CustomerList filterStatus="pending_verification" /></P>} />
            <Route path="/customers/referrals/affiliate" element={<P roles={["superadmin","admin","manager","office_staff","office","service_coordinator"]}><ReferralAffiliate /></P>} />
            <Route path="/customers/referrals/partners" element={<P roles={["superadmin","admin","manager","office_staff","office","service_coordinator"]}><PartnersList /></P>} />
            <Route path="/customers/referrals/partners/:id" element={<P roles={["superadmin","admin","manager","office_staff","office","service_coordinator"]}><PartnerProfile /></P>} />
            <Route path="/customers/referrals/performance" element={<P roles={["superadmin","admin","manager","office_staff","office","service_coordinator"]}><ReferralPerformance /></P>} />
            <Route path="/customers/:id/rewards" element={<P><CustomerRewards /></P>} />
            <Route path="/customers/wallet" element={<P><CustomerWalletManagement /></P>} />
            <Route path="/customers/:id" element={<P><CustomerDetail /></P>} />

            {/* Modules 5-10 */}
            <Route path="/operations" element={<P><Operations /></P>} />
            <Route path="/operations/service-requests" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ServiceRequestList /></P>} />
            <Route path="/operations/sr-alerts" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><SrAlertHistory /></P>} />
            <Route path="/operations/assignments" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><Assignments /></P>} />
            <Route path="/operations/timing" element={<P roles={["superadmin","admin","manager","service_coordinator"]}><OperationsTiming /></P>} />
            <Route path="/operations/skip-analytics" element={<P roles={["superadmin","admin","manager","service_coordinator"]}><SkipAnalytics /></P>} />
            <Route path="/operations/service-requests/daily" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><DailyServiceRequests /></P>} />
            <Route path="/operations/service-requests/new" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ServiceRequestForm /></P>} />
            <Route path="/operations/service-requests/:id" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ServiceRequestDetail /></P>} />
            <Route path="/operations/service-requests/:id/edit" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ServiceRequestForm /></P>} />
            <Route path="/operations/routes" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><RouteJobsList /></P>} />
            <Route path="/operations/routes/daily" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><RouteDailyView /></P>} />
            <Route path="/operations/routes/new" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><RouteCreate /></P>} />
            <Route path="/operations/routes/ai-suggest" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><AISuggestRoutes /></P>} />
            <Route path="/operations/routes/:id" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><RouteJobDetail /></P>} />
            <Route path="/live" element={<P roles={["superadmin"]}><LiveDashboard /></P>} />
            <Route path="/findgps" element={<P roles={["superadmin"]}><FindGpsDashboard /></P>} />

            {/* Gig Partner marketplace (admin surface) */}
            <Route path="/admin/gig-partners" element={<P roles={["superadmin","admin","manager","onboarding_manager"]}><GigPartnersAdmin /></P>} />
            <Route path="/admin/onboarding-managers" element={<P roles={["superadmin","admin","manager"]}><OnboardingManagersAdmin /></P>} />
            <Route path="/finance/partner-payouts" element={<P roles={["superadmin","admin","manager"]}><PartnerPayouts /></P>} />

            {/* Gig Partner app (standalone — phone OTP, no admin layout) */}
            <Route path="/partner-app/login" element={<PartnerAppLogin />} />
            <Route path="/partner-app" element={<PartnerAppShell />} />
            <Route path="/staff" element={<P><StaffList /></P>} />
            <Route path="/workforce/leaderboard" element={<P><WorkforceLeaderboard /></P>} />
            <Route path="/workforce/shifts" element={<P roles={["superadmin","admin","manager"]}><WorkforceShifts /></P>} />
            <Route path="/workforce/payroll" element={<P roles={["superadmin","admin","manager"]}><WorkforcePayroll /></P>} />
            <Route path="/workforce/staff/:id" element={<P><WorkforceStaffProfile /></P>} />
            <Route path="/fleet" element={<P><FleetMgmt /></P>} />
            <Route path="/qr" element={<P><QrInventory /></P>} />
            <Route path="/disposal" element={<P><DisposalCenter /></P>} />

            {/* Government Portal (role=govt, read-only) */}
            <Route path="/gov/login" element={<GovLogin />} />
            <Route path="/gov" element={<GovLayout/>}>
              <Route index element={<GovRegulatoryDashboard/>}/>
              <Route path="customers" element={<GovCustomers/>}/>
              <Route path="map" element={<GovComplianceMap/>}/>
              <Route path="waste" element={<GovWasteReport/>}/>
              <Route path="bmw" element={<GovBMWReports/>}/>
              <Route path="compliance" element={<GovCompliance/>}/>
              <Route path="audit" element={<GovAuditTrail/>}/>
              <Route path="certificates" element={<GovCertificateVerification/>}/>
              <Route path="sustainability" element={<GovSustainability/>}/>
              <Route path="profile" element={<GovProfile/>}/>
              <Route path="complaints" element={<GovComplaints/>}/>
              <Route path="airq" element={<GovAirq/>}/>
              <Route path="csr" element={<GovCsr/>}/>
              <Route path="legacy-dashboard" element={<GovDashboard/>}/>
            </Route>

            {/* Government User Management (admin-only) */}
            <Route path="/admin/gov-users" element={<P roles={["superadmin","admin","manager"]}><GovUserManagement/></P>}/>

            {/* Training Partners (admin-only) + Partner Portal + Hospital Landing */}
            <Route path="/admin/training-partners" element={<P roles={["superadmin","admin","manager"]}><TrainingPartners/></P>}/>
            <Route path="/admin/content" element={<P roles={["superadmin","admin"]}><ContentManagement/></P>}/>
            <Route path="/admin/content/services-page" element={<P roles={["superadmin","admin"]}><ServicesPageCms/></P>}/>
            <Route path="/admin/content/page-images" element={<P roles={["superadmin","admin"]}><PageImagesCms/></P>}/>
            <Route path="/admin/customer-app-cms" element={<P roles={["superadmin","admin"]}><CustomerAppCms/></P>}/>
            <Route path="/admin/eagleeye" element={<P roles={["superadmin","admin"]}><EagleEye/></P>}/>
            <Route path="/admin/ad-banners" element={<P roles={["superadmin","admin"]}><AdBannerManager/></P>}/>
            <Route path="/admin/alarm-sounds" element={<P roles={["superadmin","admin"]}><AlarmSoundManager/></P>}/>
            <Route path="/admin/media" element={<P roles={["superadmin","admin"]}><MediaLibrary/></P>}/>
            <Route path="/admin/site-pages" element={<P roles={["superadmin","admin"]}><SitePages/></P>}/>
            <Route path="/admin/hospitals" element={<P roles={["superadmin","admin"]}><Hospitals/></P>}/>
            <Route path="/admin/about" element={<P roles={["superadmin","admin"]}><AdminAboutEditor/></P>}/>
            <Route path="/admin/otp-settings" element={<P roles={["superadmin","admin"]}><AdminOTPSettings/></P>}/>
            <Route path="/admin/nav-menus" element={<P roles={["superadmin","admin"]}><AdminNavMenus/></P>}/>
            <Route path="/admin/institutional-certs" element={<P roles={["superadmin","admin"]}><InstitutionalCerts/></P>}/>
            <Route path="/admin/data-sync" element={<P roles={["superadmin"]}><DataSync/></P>}/>
            <Route path="/admin/careers" element={<P roles={["superadmin","admin"]}><AdminCareers/></P>}/>
            <Route path="/admin/manual-payments" element={<P roles={["superadmin","admin","manager"]}><AdminManualPayments/></P>}/>
            <Route path="/admin/agencies" element={<P roles={["superadmin","admin"]}><AdminAgencies/></P>}/>
            <Route path="/admin/registration-certs" element={<P roles={["superadmin","admin"]}><AdminRegCerts/></P>}/>
            <Route path="/customers/registration-certs" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><CustomerRegistrationCerts/></P>}/>
            <Route path="/admin/service-partners" element={<P roles={["superadmin","admin"]}><ServicePartnersAdmin/></P>}/>

            {/* Service Partner portal (Retail / Health / Delivery) — separate auth */}
            <Route path="/service-partner/login" element={<PartnerPortalLogin/>}/>
            <Route path="/service-partner/dashboard" element={<PartnerPortalDashboard/>}/>
            <Route path="/service-partner/listings" element={<PartnerListings/>}/>
            <Route path="/service-partner/orders" element={<PartnerOrders/>}/>
            <Route path="/service-partner/deliveries" element={<PartnerDeliveries/>}/>
            <Route path="/service-partner/health" element={<PartnerHealth/>}/>
            <Route path="/service-partner/wave4" element={<PartnerWave4/>}/>

            {/* Courier mobile portal — separate auth (magic-link → JWT) */}
            <Route path="/delivery-app/auth" element={<CourierAuthExchange/>}/>
            <Route path="/delivery-app/login" element={<CourierLogin/>}/>
            <Route path="/delivery-app" element={<CourierHome/>}/>
            <Route path="/delivery-app/tasks/:id" element={<CourierTaskDetail/>}/>
            <Route path="/partner/login" element={<PartnerLogin/>}/>
            <Route path="/training/hospital/:slug/login" element={<PartnerBrandedLogin/>}/>
            <Route path="/partner-portal" element={<PartnerLayout/>}>
              <Route index element={<PartnerOverview/>}/>
              <Route path="users" element={<PartnerUsers/>}/>
              <Route path="programs" element={<PartnerPrograms/>}/>
              <Route path="progress" element={<PartnerProgress/>}/>
              <Route path="certificates" element={<PartnerCertificates/>}/>
              <Route path="branding" element={<PartnerBranding/>}/>
              <Route path="billing" element={<PartnerBilling/>}/>
              <Route path="reports" element={<PartnerReports/>}/>
              <Route path="audit" element={<PartnerAudit/>}/>
            </Route>
            <Route path="/training/hospital/:slug" element={<HospitalLanding/>}/>

            {/* Staff Management Module (superadmin / admin / hr) */}
            <Route path="/admin/staff-mgmt" element={<P roles={["superadmin","admin","hr"]}><OfficeStaffList/></P>}/>
            <Route path="/admin/staff-mgmt/new" element={<P roles={["superadmin","admin","hr"]}><OfficeStaffForm/></P>}/>
            <Route path="/admin/staff-mgmt/:id" element={<P roles={["superadmin","admin","hr"]}><OfficeStaffProfile/></P>}/>
            <Route path="/admin/staff-mgmt/:id/edit" element={<P roles={["superadmin","admin","hr"]}><OfficeStaffForm/></P>}/>
            <Route path="/admin/staff-mgmt/roles" element={<P roles={["superadmin","admin","hr"]}><RolesPermissions/></P>}/>
            <Route path="/admin/staff-mgmt/attendance" element={<P roles={["superadmin","admin","hr"]}><OfficeAttendance/></P>}/>
            <Route path="/admin/staff-mgmt/logs" element={<P roles={["superadmin","admin","hr"]}><BreakActivityLogs/></P>}/>
            <Route path="/admin/staff-mgmt/performance" element={<P roles={["superadmin","admin","hr","manager"]}><PerformanceTracking/></P>}/>
            <Route path="/admin/staff-mgmt/payroll" element={<P roles={["superadmin","admin","hr"]}><OfficePayrollSummary/></P>}/>
            <Route path="/admin/my-duty" element={<P><MyDuty/></P>}/>

            {/* Module 11 Fraud */}
            <Route path="/fraud" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><FraudDashboard /></P>} />

            {/* Module 12 Finance */}
            <Route path="/finance/invoices" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><Invoices /></P>} />
            <Route path="/finance/thermal/:id" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><InvoiceThermal /></P>} />
            <Route path="/finance/invoice-analytics" element={<P roles={["superadmin","admin","manager","finance","accountant"]}><InvoiceAnalytics /></P>} />
            <Route path="/finance/invoices-pending" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><InvoicesPending /></P>} />
            <Route path="/finance/transactions" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><Transactions /></P>} />
            <Route path="/finance/payments" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><RazorpayPayments /></P>} />
            <Route path="/finance/wallet-txns" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><WalletTransactions /></P>} />
            <Route path="/health-services" element={<P roles={["superadmin","admin","manager"]}><HealthServicesCommand /></P>} />
            <Route path="/ambulance/dispatch" element={<P roles={["superadmin","admin","manager","service_coordinator"]}><AmbulanceDispatch /></P>} />
            <Route path="/analytics/daily-care" element={<P roles={["superadmin","admin","manager"]}><DailyCareAnalytics /></P>} />
            <Route path="/health-services/extras" element={<P roles={["superadmin","admin","manager"]}><HealthOpsExtras /></P>} />
            <Route path="/health-services/pharmacy-ops" element={<P roles={["superadmin","admin","manager","service_coordinator"]}><PharmacyOpsConsole /></P>} />
            <Route path="/health-services/wave3" element={<P roles={["superadmin","admin","manager","finance"]}><Wave3Console /></P>} />
            <Route path="/health-services/wave4" element={<P roles={["superadmin","admin","manager","finance"]}><Wave4Console /></P>} />
            <Route path="/health-services/airq/superadmin" element={<P roles={["superadmin","admin"]}><AirqSuperAdmin /></P>} />

            {/* AirQ account portal (pad_operator + aeromed_admin — separate auth) */}
            <Route path="/airq" element={<AirqLanding />} />
            <Route path="/airq/login" element={<AirqAccountLogin />} />
            <Route path="/airq/gov-login" element={<AirqGovLogin />} />
            <Route path="/airq/dashboard" element={<AirqAccountDashboard />} />
            <Route path="/me/op-book" element={<OpBookingWidget />} />
            <Route path="/nurse-app" element={<NurseMobileView />} />
            <Route path="/amb-public" element={<AmbulancePublicPortal />} />
            <Route path="/finance/emails" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><EmailLog /></P>} />
            <Route path="/finance/expenses" element={<P roles={["superadmin","admin","manager","office_staff","office","service_coordinator"]}><Expenses /></P>} />
            <Route path="/operations/daily-briefing" element={<P roles={["superadmin","admin","manager"]}><DailyBriefing /></P>} />
            <Route path="/finance/revenue" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><Revenue /></P>} />
            <Route path="/finance/rollup" element={<P roles={["superadmin","admin","manager","office_staff","office"]}><FinanceRollup /></P>} />
            <Route path="/finance/settings" element={<P roles={["superadmin","admin"]}><FinanceSettings /></P>} />

            {/* Module 4 CRM */}
            <Route path="/crm/leads" element={<P><Leads /></P>} />
            <Route path="/crm/contact-submissions" element={<P roles={["superadmin","admin","manager"]}><ContactSubmissions /></P>} />
            <Route path="/crm/tickets" element={<P><Tickets /></P>} />
            <Route path="/crm/tasks" element={<P><CrmTasks /></P>} />
            <Route path="/crm/chats" element={<P><Chats /></P>} />
            <Route path="/crm/handoff-queue" element={<P><HandoffQueue /></P>} />
            <Route path="/crm/phone" element={<P><PhoneCRM /></P>} />
            <Route path="/crm/phone/report" element={<P><PhoneReport /></P>} />
            <Route path="/crm/phone/passcodes" element={<P><PhonePasscodes /></P>} />
            <Route path="/settings/chat-bot" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ChatBotSettings /></P>} />
            <Route path="/settings/reply-templates" element={<P roles={["superadmin","admin","manager","service_coordinator","office_staff","office"]}><ReplyTemplates /></P>} />
            <Route path="/crm/tickets/archive" element={<P roles={["superadmin"]}><TicketsArchive /></P>} />
            <Route path="/admin/audit/ticket-purges" element={<P roles={["superadmin"]}><AuditTicketPurges /></P>} />

            {/* Module 13 Analytics */}
            <Route path="/analytics" element={<P roles={["superadmin","admin","manager"]}><Analytics /></P>} />

            {/* Module 14 Certificates */}
            <Route path="/certificates" element={<P><Certificates /></P>} />

            {/* Module 15 System Settings */}
            <Route path="/settings" element={<P roles={["superadmin","admin"]}><SystemSettings /></P>} />
            <Route path="/settings/printer" element={<P roles={["superadmin","admin","manager","office_staff","office","hr","service_coordinator"]}><PrinterSettings /></P>} />
            <Route path="/settings/scale" element={<P roles={["superadmin","admin","manager","office_staff","office","hr","service_coordinator"]}><ScaleSettings /></P>} />

            {/* Module 16 Franchise */}
            <Route path="/franchise" element={<P roles={["superadmin","admin"]}><Franchise /></P>} />

            {/* System */}
            <Route path="/audit-logs" element={<P roles={["superadmin","admin","manager"]}><AuditLogs /></P>} />

            {/* Future modules (stubs) */}
            <Route path="/mod/:name" element={<P><ModuleStub /></P>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </StaffAuthProvider>
          </CustomerAuthProvider>
        </AuthProvider>
        </SitePagesProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
